import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { IS_CHROME_EXTENSION } from "../constants";
import type { ClientNoteTranscriptItem } from "../types";
import storage, { chromeStorage } from "../utils/localStorage";

export interface TranscriptStore {
  transcriptItems: Record<string, ClientNoteTranscriptItem>;
  addTranscriptItem: (transcriptItem: ClientNoteTranscriptItem) => void;
  clearTranscriptItems: () => void;
  getNonFinalizedTranscriptItems: () => ClientNoteTranscriptItem[];
  markTranscriptItemsFinal: (items: ClientNoteTranscriptItem[]) => void;
}

export const transcriptStore = create<TranscriptStore>()(
  persist(
    immer((set, get) => ({
      transcriptItems: {},
      addTranscriptItem: (transcriptItem) =>
        set((state) => {
          state.transcriptItems[transcriptItem.id] = transcriptItem;
        }),
      clearTranscriptItems: () =>
        set((state) => {
          state.transcriptItems = {};
        }),
      getNonFinalizedTranscriptItems: () => {
        return Object.values(get().transcriptItems).filter(
          (item) => !item.is_final,
        );
      },
      markTranscriptItemsFinal: (items) => {
        if (items.length === 0) return;
        set((state) => {
          for (const item of items) {
            const current = state.transcriptItems[item.id];
            if (current) {
              current.is_final = true;
            }
          }
        });
      },
    })),
    {
      name: "transcript-store",
      storage: createJSONStorage(() =>
        IS_CHROME_EXTENSION ? chromeStorage : storage,
      ),
      partialize: () => ({}),
      onRehydrateStorage: () => () => {
        return void 0;
      },
    },
  ),
);

export const useTranscriptStore = transcriptStore;

export function useTranscriptItemsList(): ClientNoteTranscriptItem[] {
  const transcriptItems = useTranscriptStore((state) => state.transcriptItems);
  return [...Object.values(transcriptItems)]
    .filter((item) => item.text)
    .sort((a, b) => a.start_offset_ms - b.start_offset_ms);
}

export function useAddTranscriptItem(): (
  transcriptItem: ClientNoteTranscriptItem,
) => void {
  return useTranscriptStore((state) => state.addTranscriptItem);
}

export function useClearTranscriptItems(): () => void {
  return useTranscriptStore((state) => state.clearTranscriptItems);
}
